/**
 * Notifications (cahier §12, ADR-010, ADR-035) : événements, canaux, modèles
 * par défaut, rendu, contrôles de contenu et décompte SMS. Partagé entre l'API
 * (envoi, validation) et le portail admin (éditeur, aperçu).
 *
 * 🔒 Contenu minimal : un courriel ou un SMS n'expose jamais de note, de rang,
 * de motif détaillé ni de secret ; il invite à se connecter. Le centre de
 * notifications du portail (espace authentifié) peut être un peu plus précis
 * (variables « portail seulement »).
 */
export const NOTIFICATION_CHANNELS = {
    portal: 'Portail',
    email: 'Courriel',
    sms: 'SMS',
};
export const NOTIFICATION_CHANNEL_CODES = Object.keys(NOTIFICATION_CHANNELS);
export const NOTIFICATION_STATUSES = {
    pending: 'En attente',
    sent: 'Envoyée',
    failed: 'Échec',
    skipped: 'Non envoyée',
};
/** Variables utilisables dans les modèles (libellé affiché dans l'éditeur). */
export const NOTIFICATION_VARIABLES = {
    prenom: 'Prénom du destinataire',
    concours: 'Intitulé du concours',
    matricule: 'Matricule de candidature',
    reference: 'Référence du paiement',
    etape: 'Étape de sélection',
    date: 'Date (heure de Dakar)',
    echeance: 'Échéance',
    decision: 'Décision de recevabilité',
    lieu: 'Centre, salle et place',
    lien: 'Lien vers l’espace candidat',
};
export const NOTIFICATION_EVENTS = {
    'account.created': {
        label: 'Création de compte',
        description: 'Compte candidat activé (adresse vérifiée).',
        variables: ['prenom', 'lien'],
        sms: false,
    },
    'application.submitted': {
        label: 'Confirmation de dépôt',
        description: 'Dossier soumis : le récépissé est disponible.',
        variables: ['prenom', 'concours', 'matricule', 'lien'],
        sms: false,
    },
    'payment.confirmed': {
        label: 'Paiement confirmé',
        description: 'Le paiement des frais de candidature est confirmé.',
        variables: ['prenom', 'concours', 'matricule', 'reference', 'lien'],
        sms: false,
    },
    'payment.failed': {
        label: 'Paiement échoué',
        description: 'Le paiement n’a pas abouti : le candidat peut réessayer.',
        variables: ['prenom', 'concours', 'matricule', 'reference', 'lien'],
        sms: false,
    },
    'application.complement_requested': {
        label: 'Demande de complément',
        description: 'Des pièces ou informations sont demandées avant une échéance.',
        variables: ['prenom', 'concours', 'matricule', 'echeance', 'lien'],
        sms: true,
    },
    'application.admissibility_decided': {
        label: 'Décision de recevabilité',
        description: 'Une décision (recevable ou non) a été validée sur le dossier.',
        variables: ['prenom', 'concours', 'matricule', 'lien'],
        portalVariables: ['decision'],
        sms: false,
    },
    'convocation.issued': {
        label: 'Convocation',
        description: 'Une convocation est disponible dans l’espace candidat.',
        variables: ['prenom', 'concours', 'etape', 'date', 'lien'],
        portalVariables: ['lieu'],
        sms: true,
    },
    'convocation.cancelled': {
        label: 'Convocation annulée',
        description: 'La convocation a été annulée ; une nouvelle suivra.',
        variables: ['prenom', 'concours', 'etape', 'lien'],
        sms: true,
    },
    'competition.calendar_changed': {
        label: 'Modification du calendrier',
        description: 'Les dates du concours ont changé.',
        variables: ['prenom', 'concours', 'lien'],
        sms: false,
    },
    'results.published': {
        label: 'Publication des résultats',
        description: 'Des résultats sont publiés ; le détail est dans l’espace candidat.',
        variables: ['prenom', 'concours', 'etape', 'lien'],
        sms: true,
    },
    'competition.closed': {
        label: 'Clôture des dépôts',
        description: 'Les dépôts sont clos (candidats ayant un dossier non soumis).',
        variables: ['prenom', 'concours', 'lien'],
        sms: false,
    },
    'competition.extended': {
        label: 'Prolongation des dépôts',
        description: 'La date de clôture des dépôts est repoussée.',
        variables: ['prenom', 'concours', 'date', 'lien'],
        sms: false,
    },
};
export const NOTIFICATION_EVENT_CODES = Object.keys(NOTIFICATION_EVENTS);
const CONNECT = 'Connectez-vous à votre espace candidat pour consulter le détail : {lien}';
/** Modèles par défaut (une personnalisation enregistrée les remplace). */
export const DEFAULT_NOTIFICATION_TEMPLATES = {
    'account.created': {
        portal: {
            enabled: true,
            title: 'Bienvenue sur la plateforme des concours',
            body: 'Votre compte est activé. Complétez votre profil avant de candidater.',
        },
        email: {
            enabled: true,
            subject: 'Votre compte candidat est activé',
            body: 'Bonjour {prenom},\n\nVotre compte sur la plateforme des concours de l’Administration pénitentiaire est activé. Complétez votre profil avant de déposer une candidature.\n\nVotre espace : {lien}',
        },
        sms: { enabled: false, body: '' },
    },
    'application.submitted': {
        portal: {
            enabled: true,
            title: 'Dossier déposé',
            body: 'Votre dossier {matricule} ({concours}) est déposé. Téléchargez votre récépissé.',
        },
        email: {
            enabled: true,
            subject: 'Dossier {matricule} déposé',
            body: `Bonjour {prenom},\n\nVotre dossier {matricule} pour le concours « {concours} » est déposé. Votre récépissé est disponible dans votre espace.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'payment.confirmed': {
        portal: {
            enabled: true,
            title: 'Paiement confirmé',
            body: 'Le paiement {reference} du dossier {matricule} est confirmé. Votre reçu est disponible.',
        },
        email: {
            enabled: true,
            subject: 'Paiement confirmé — dossier {matricule}',
            body: `Bonjour {prenom},\n\nLe paiement {reference} des frais de candidature (concours « {concours} ») est confirmé.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'payment.failed': {
        portal: {
            enabled: true,
            title: 'Paiement non abouti',
            body: 'Le paiement {reference} du dossier {matricule} n’a pas abouti. Vous pouvez réessayer.',
        },
        email: {
            enabled: true,
            subject: 'Paiement non abouti — dossier {matricule}',
            body: `Bonjour {prenom},\n\nLe paiement {reference} (concours « {concours} ») n’a pas abouti. Aucun montant n’est dû pour cette tentative ; vous pouvez réessayer depuis votre espace.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'application.complement_requested': {
        portal: {
            enabled: true,
            title: 'Complément demandé',
            body: 'Un complément est demandé pour votre dossier {matricule} avant le {echeance}.',
        },
        email: {
            enabled: true,
            subject: 'Complément demandé — dossier {matricule}',
            body: `Bonjour {prenom},\n\nUn complément est demandé pour votre dossier {matricule} (concours « {concours} »). Il doit être fourni avant le {echeance}.\n\n${CONNECT}`,
        },
        sms: {
            enabled: true,
            body: 'Concours : un complement est demande pour votre dossier {matricule} avant le {echeance}. Consultez votre espace candidat.',
        },
    },
    'application.admissibility_decided': {
        portal: {
            enabled: true,
            title: 'Décision sur votre dossier',
            body: 'Votre dossier {matricule} a été déclaré {decision}. Consultez le détail.',
        },
        email: {
            enabled: true,
            subject: 'Décision sur votre dossier {matricule}',
            body: `Bonjour {prenom},\n\nUne décision de recevabilité a été rendue sur votre dossier {matricule} (concours « {concours} »).\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'convocation.issued': {
        portal: {
            enabled: true,
            title: 'Convocation disponible',
            body: '{etape} : vous êtes convoqué(e) le {date} — {lieu}. Téléchargez votre convocation.',
        },
        email: {
            enabled: true,
            subject: 'Convocation — {etape}',
            body: `Bonjour {prenom},\n\nVotre convocation pour l’étape « {etape} » du concours « {concours} » (le {date}) est disponible. Imprimez-la et présentez-la avec votre pièce d’identité.\n\n${CONNECT}`,
        },
        sms: {
            enabled: true,
            body: 'Concours : votre convocation ({etape}, le {date}) est disponible dans votre espace candidat.',
        },
    },
    'convocation.cancelled': {
        portal: {
            enabled: true,
            title: 'Convocation annulée',
            body: 'Votre convocation pour « {etape} » est annulée. Une nouvelle convocation vous sera adressée.',
        },
        email: {
            enabled: true,
            subject: 'Convocation annulée — {etape}',
            body: `Bonjour {prenom},\n\nVotre convocation pour l’étape « {etape} » du concours « {concours} » est annulée. Une nouvelle convocation vous sera adressée.\n\n${CONNECT}`,
        },
        sms: {
            enabled: true,
            body: 'Concours : votre convocation ({etape}) est annulee. Une nouvelle suivra. Consultez votre espace candidat.',
        },
    },
    'competition.calendar_changed': {
        portal: {
            enabled: true,
            title: 'Calendrier modifié',
            body: 'Le calendrier du concours « {concours} » a été modifié.',
        },
        email: {
            enabled: true,
            subject: 'Calendrier modifié — {concours}',
            body: `Bonjour {prenom},\n\nLe calendrier du concours « {concours} » a été modifié.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'results.published': {
        portal: {
            enabled: true,
            title: 'Résultats publiés',
            body: 'Les résultats de l’étape « {etape} » sont publiés. Consultez « Mes résultats ».',
        },
        email: {
            enabled: true,
            subject: 'Résultats publiés — {concours}',
            body: `Bonjour {prenom},\n\nLes résultats de l’étape « {etape} » du concours « {concours} » sont publiés.\n\n${CONNECT}`,
        },
        sms: {
            enabled: true,
            body: 'Concours : les resultats ({etape}) sont publies. Consultez votre espace candidat.',
        },
    },
    'competition.closed': {
        portal: {
            enabled: true,
            title: 'Dépôts clos',
            body: 'Les dépôts du concours « {concours} » sont clos : votre dossier non soumis ne peut plus être déposé.',
        },
        email: {
            enabled: true,
            subject: 'Dépôts clos — {concours}',
            body: `Bonjour {prenom},\n\nLes dépôts du concours « {concours} » sont clos. Votre dossier, resté non soumis, ne peut plus être déposé.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
    'competition.extended': {
        portal: {
            enabled: true,
            title: 'Dépôts prolongés',
            body: 'Les dépôts du concours « {concours} » sont prolongés jusqu’au {date}.',
        },
        email: {
            enabled: true,
            subject: 'Dépôts prolongés — {concours}',
            body: `Bonjour {prenom},\n\nLa date limite de dépôt du concours « {concours} » est repoussée au {date}. Pensez à soumettre votre dossier.\n\n${CONNECT}`,
        },
        sms: { enabled: false, body: '' },
    },
};
/** Remplace `{variable}` ; une variable inconnue reste telle quelle (détectée par la validation). */
export function renderNotification(template, values) {
    return template.replace(/\{([a-z_]+)\}/g, (match, key) => {
        const value = values[key];
        return value === undefined ? match : value;
    });
}
export const variablesOf = (text) => [...text.matchAll(/\{([a-z_]+)\}/g)].map((m) => m[1]);
/**
 * Formulations sensibles interdites dans un message sortant : note sur 20,
 * rang, mot de passe, code secret. Volontairement prudent (faux positifs
 * préférables à une fuite).
 */
const SENSITIVE_PATTERNS = [
    { pattern: /\d+(?:[.,]\d+)?\s*\/\s*20\b/, message: 'Une note (sur 20) ne doit pas figurer.' },
    {
        pattern: /\bnotes?\s+(?:obtenue|de\s+\d|:\s*\d)/i,
        message: 'Une note ne doit pas figurer dans le message.',
    },
    {
        pattern: /\b(?:rang|class[ée]e?)\s*(?::|n°|\d)/i,
        message: 'Un rang ou un classement ne doit pas figurer.',
    },
    { pattern: /\bmot de passe\b/i, message: 'Aucun mot de passe ne doit figurer.' },
    { pattern: /\bcode (?:secret|pin)\b/i, message: 'Aucun code secret ne doit figurer.' },
];
export function sensitiveContent(text) {
    return SENSITIVE_PATTERNS.filter((p) => p.pattern.test(text)).map((p) => p.message);
}
// Alphabet GSM 03.38 (caractères de base, puis extension comptée double).
const GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
const GSM_EXTENDED = '^{}\\[~]|€';
/** Longueur et nombre de segments d'un SMS (GSM-7 : 160/153 ; Unicode : 70/67). */
export function smsInfo(text) {
    let units = 0;
    let unicode = false;
    for (const ch of text) {
        if (GSM_BASIC.includes(ch))
            units += 1;
        else if (GSM_EXTENDED.includes(ch))
            units += 2;
        else
            unicode = true;
    }
    const length = unicode ? [...text].length : units;
    const [single, multi] = unicode ? [70, 67] : [160, 153];
    const segments = length === 0 ? 0 : length <= single ? 1 : Math.ceil(length / multi);
    return { length, unicode, segments };
}
export const SMS_MAX_SEGMENTS = 3;
/** Contrôles d'un modèle avant enregistrement (API) et en direct (éditeur). */
export function validateNotificationTemplate(event, t) {
    const def = NOTIFICATION_EVENTS[event];
    const problems = [];
    const shared = new Set(def.variables);
    const portal = new Set([...def.variables, ...(def.portalVariables ?? [])]);
    const check = (field, text, allowed, outbound) => {
        for (const v of variablesOf(text))
            if (!allowed.has(v))
                problems.push({
                    field,
                    message: def.portalVariables?.includes(v)
                        ? `La variable {${v}} est réservée au portail.`
                        : `Variable inconnue pour cet événement : {${v}}.`,
                });
        if (outbound)
            for (const m of sensitiveContent(text))
                problems.push({ field, message: m });
    };
    if (t.portal.enabled) {
        if (!t.portal.title.trim())
            problems.push({ field: 'portal.title', message: 'Titre requis.' });
        if (!t.portal.body.trim())
            problems.push({ field: 'portal.body', message: 'Texte requis.' });
    }
    check('portal.title', t.portal.title, portal, false);
    check('portal.body', t.portal.body, portal, false);
    if (t.email.enabled) {
        if (!t.email.subject.trim())
            problems.push({ field: 'email.subject', message: 'Objet requis.' });
        if (!t.email.body.trim())
            problems.push({ field: 'email.body', message: 'Texte requis.' });
    }
    check('email.subject', t.email.subject, shared, true);
    check('email.body', t.email.body, shared, true);
    if (t.sms.enabled) {
        if (!def.sms)
            problems.push({
                field: 'sms.enabled',
                message: 'Le SMS est réservé à l’OTP, la convocation, les résultats et le complément.',
            });
        if (!t.sms.body.trim())
            problems.push({ field: 'sms.body', message: 'Texte requis.' });
        // La longueur réelle dépend des valeurs : on compte le texte avec des valeurs typiques.
        const sample = renderNotification(t.sms.body, SAMPLE_VALUES);
        if (smsInfo(sample).segments > SMS_MAX_SEGMENTS)
            problems.push({
                field: 'sms.body',
                message: `SMS trop long : ${SMS_MAX_SEGMENTS} segments au plus.`,
            });
    }
    check('sms.body', t.sms.body, shared, true);
    if (!t.portal.enabled && !t.email.enabled && !t.sms.enabled)
        problems.push({ field: 'portal.enabled', message: 'Au moins un canal doit rester actif.' });
    return problems;
}
/** Valeurs d'exemple (aperçu de l'éditeur, estimation de la longueur des SMS). */
export const SAMPLE_VALUES = {
    prenom: 'Awa',
    concours: 'Concours direct d’agents pénitentiaires',
    matricule: 'PEN-2026-K7M2QX',
    reference: 'PAY-7KQ2M9XR4T',
    etape: 'Épreuves écrites',
    date: '20/11/2026 à 08h00',
    echeance: '15/11/2026 à 23h59',
    decision: 'recevable',
    lieu: 'CFPA de Dakar, salle 2, place 14',
    lien: 'https://concours.example.sn/espace',
};
/* ------------------------------ Envois groupés ------------------------------ */
export const CAMPAIGN_AUDIENCES = {
    drafts: 'Dossiers non soumis',
    submitted: 'Dossiers déposés',
    admissible: 'Candidats recevables',
    stage: 'Candidats d’une étape',
};
/** Canaux d'un envoi groupé : pas de SMS (ADR-010, réservé aux événements clés). */
export const CAMPAIGN_CHANNELS = ['portal', 'email'];
export const NOTIFICATION_PROVIDERS = {
    smtp: {
        kind: 'email',
        label: 'Serveur SMTP',
        description: 'Envoi des courriels par un serveur SMTP (messagerie de l’administration ou prestataire).',
        fields: [
            { key: 'host', label: 'Serveur', secret: false, required: true },
            {
                key: 'port',
                label: 'Port',
                secret: false,
                required: true,
                help: '587 (STARTTLS) ou 465 (TLS).',
            },
            {
                key: 'secure',
                label: 'TLS direct (oui / non)',
                secret: false,
                required: false,
                help: '« oui » pour le port 465.',
            },
            { key: 'username', label: 'Identifiant', secret: false, required: false },
            { key: 'password', label: 'Mot de passe', secret: true, required: false },
            { key: 'fromAddress', label: 'Adresse d’expédition', secret: false, required: true },
            { key: 'fromName', label: 'Nom d’expéditeur', secret: false, required: false },
        ],
    },
    orange_sms: {
        kind: 'sms',
        label: 'Orange SMS API',
        description: 'API SMS d’Orange (Sonatel) : jeton OAuth (client credentials), puis envoi depuis le numéro expéditeur du contrat.',
        fields: [
            { key: 'clientId', label: 'Client ID (OAuth)', secret: false, required: true },
            { key: 'clientSecret', label: 'Client secret', secret: true, required: true },
            {
                key: 'senderAddress',
                label: 'Numéro expéditeur',
                secret: false,
                required: true,
                help: 'Au format international, ex. +221770000000 (celui du contrat).',
            },
            {
                key: 'senderName',
                label: 'Nom affiché',
                secret: false,
                required: false,
                help: 'Si le contrat l’autorise (ex. DAP-CONCOURS).',
            },
        ],
    },
};
export const NOTIFICATION_PROVIDER_DRIVERS = Object.keys(NOTIFICATION_PROVIDERS);
//# sourceMappingURL=notifications.js.map